import React, { useState } from 'react';
import { motion } from "motion/react";
import type { GroupedScheduleResponse } from '@/features/booking/types/bookingTypes';
import type { BookingTransfer } from '@/features/booking/types/transferTypes';
import { useTransferList } from '@/features/booking/hooks/useTransferList';
import { TransferCard } from './TransferCard';

interface TransferTicketsViewProps {
    groupedSchedules: GroupedScheduleResponse[];
    allTransfers: BookingTransfer[];
}

export const TransferTicketsView: React.FC<TransferTicketsViewProps> = ({
    groupedSchedules,
    allTransfers
}) => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const {
        sortedBookings,
        getTransferDisplayInfo,
        formatDate,
        formatTime,
        formatExpiresAt,
    } = useTransferList(groupedSchedules, allTransfers);

    const handleCopyLink = async (transferId: string) => {
        const link = `${window.location.origin}/transfer-booking/t/${transferId}`;
        try {
            await navigator.clipboard.writeText(link);
            setCopiedId(transferId);
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            // Silently fail
        }
    };

    if (sortedBookings.length === 0) {
        return (
            <motion.div
                className="text-center py-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
            >
                <motion.p
                    className="text-gray-400"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                >
                    You don't have any transfers.
                </motion.p>
            </motion.div>
        );
    }

    return (
        <motion.div
            className="space-y-3 sm:space-y-4 lg:space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
        >
            {sortedBookings.map((item) => {
                const displayInfo = getTransferDisplayInfo(item.transferId);

                return (
                    <TransferCard
                        key={item.booking.id || item.transferId}
                        item={item}
                        displayInfo={displayInfo}
                        expiresAtFormatted={displayInfo.expiresAt ? formatExpiresAt(displayInfo.expiresAt) : undefined}
                        dateFormatted={formatDate(item.eventDate)}
                        timeFormatted={formatTime(item.eventDate)}
                        isCopied={copiedId === item.transferId}
                        onCopyLink={() => handleCopyLink(item.transferId)}
                    />
                );
            })}
        </motion.div>
    );
};

export default TransferTicketsView;
